import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import OptimizedVideo from '../components/OptimizedVideo';
import OptimizedImage from '../components/OptimizedImage';

/* ───── Animate hook ───── */
const useAnimateIn = (threshold = 0.15) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, vis];
};

/* ════════════ PART 1 — DECCAN.AI HERO (dark, video bg) ════════════ */
const DeccanHero = () => (
  <section className="relative min-h-[90vh] flex items-center bg-black overflow-hidden">
    {/* Background video */}
    <OptimizedVideo
      src="https://cdn.prod.website-files.com/66d017bee914d25aa0a45529%2F67e165d34772f24b9da5218c_render%201%20hero%20slow-transcode.mp4"
      poster="https://cdn.prod.website-files.com/66d017bee914d25aa0a45529%2F67e165d34772f24b9da5218c_render%201%20hero%20slow-poster-00001.jpg"
      className="absolute inset-0 w-full h-full object-cover opacity-40"
      autoPlay
      loop
      muted
      playsInline
      priority
    />
    <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-16 py-32 lg:py-44">
      <p className="text-white/70 text-[16px] mb-4">AI Agents Built for Banking & Retail</p>
      <h1 className="text-white text-[40px] sm:text-[56px] lg:text-[72px] font-light leading-[1.05] tracking-[-0.03em] mb-6">
        AI Agents,{' '}
        <span className="bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent">
          Built for Your Business{' '}
        </span>
        Not the Other Way Around
      </h1>
      <p className="text-white/60 text-[18px] lg:text-[20px] max-w-[720px] leading-[1.5] mb-10">
        Custom-built AI agents adapt to your business needs, rather than forcing your business to adjust to a rigid, pre-built AI system.
      </p>
      <Link to="/get-in-touch" className="inline-block px-8 py-3 border border-white/30 text-white rounded-full text-[18px] hover:bg-white/10 transition-colors">
        Talk to us
      </Link>
    </div>
  </section>
);

/* ════════════ DECCAN PILLARS (dark cards) ════════════ */
const DeccanPillars = () => {
  const [ref, vis] = useAnimateIn();
  const items = [
    { title: 'Not Just Another AI Platform', desc: "We've trained the best LLMs out there." },
    { title: 'Deep Expertise, Not One-Size-Fits-All', desc: 'Custom AI agents designed to fit your business, not the other way around.' },
    { title: 'Results in Days, Not Months', desc: 'Achieve in days what internal teams take months to deliver.' },
  ];
  return (
    <section ref={ref} className="bg-[#0a0a0a] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16 grid md:grid-cols-3 gap-6">
        {items.map((item, i) => (
          <div key={i} className={`bg-[#1a1a1a] border border-white/10 rounded-xl p-8 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 150}ms` }}>
            <h3 className="text-white text-[20px] lg:text-[24px] font-medium mb-3">{item.title}</h3>
            <p className="text-white/50 text-[15px] leading-[1.6]">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ════════════ PART 2 — ADEPT STACK (light, image+text rows) ════════════ */
const stackItems = [
  { img: 'https://adept.ai/_astro/stack-data.CN99oMgz_ZYNpBN.png', title: 'Proprietary agent training data', desc: 'Trillions of tokens specific to web UIs and real software usage.' },
  { img: 'https://adept.ai/_astro/stack-models.C4ohYDTv_1IajTu.png', title: 'Suite of multimodal models', desc: 'Exceptional at key agentic behaviors that matter to enterprises: localization, web understanding, and planning.' },
  { img: 'https://adept.ai/_astro/stack-actuation.B5mz--Te_1mp1Mu.png', title: 'Custom actuation software', desc: 'Powered by a proprietary DSL and actuation layer that enables actions across websites and software applications.' },
  { img: 'https://adept.ai/_astro/stack-feedback.BXlmt7CG_ZkJspn.png', title: 'Feedback & data collection tools', desc: 'A suite of custom tools for intuitive and easy ongoing model improvement.' },
];

const AdeptStack = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <div className="flex flex-col md:flex-row gap-5 items-baseline mb-10">
          <div className="md:w-1/3">
            <OptimizedImage src="https://adept.ai/_astro/agent-stack-logo.eb-HTB0a_ZrePvx.png" alt="Agent Stack" className="w-[280px]" />
          </div>
          <p className="md:w-2/3 text-[#888] text-[20px] lg:text-[24px] tracking-tight leading-tight">
            Building useful, reliable agents requires a full-stack approach
          </p>
        </div>
        <div className="flex flex-col gap-10">
          {stackItems.map((s, i) => (
            <div key={i} className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-12 bg-[#F5F3F0] rounded-xl p-6 lg:p-10 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 120}ms` }}>
              <div className="w-full lg:w-1/3 aspect-[3/2] rounded-lg overflow-hidden shadow-lg">
                <OptimizedImage src={s.img} alt={s.title} className="w-full h-full object-cover" />
              </div>
              <div className="w-full lg:w-2/3">
                <h2 className="text-[#272422] text-[24px] lg:text-[36px] font-medium tracking-tighter leading-tight mb-3">{s.title}</h2>
                <p className="text-[#272422] text-[16px] lg:text-[20px] tracking-tight">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════ ADEPT CAPABILITIES (light, eval scores) ════════════ */
const capabilities = [
  { img: 'https://adept.ai/_astro/locate.CsgNHxVA_1woAyQ.png', title: 'Locate', score: 93, label: 'Adept Locate', desc: 'Accurately locate items on a webpage or application, like buttons, links, text fields, and more.' },
  { img: 'https://adept.ai/_astro/web-vqa.CNQIUMqF_ZUd01i.png', title: 'Web VQA', score: 88.2, label: 'Adept Web VQA', desc: 'Reason and answer questions about websites, documents, PDFs, charts, graphs, and tables.' },
  { img: 'https://adept.ai/_astro/planning.Df-CodaZ_Z1W3Wuc.png', title: 'End-to-end workflows', score: 88, label: 'Adept Planning', desc: 'Successfully plan and execute complex enterprise end-to-end workflows.', comparison: { label: 'GPT-4', score: 59 } },
];

const AdeptCapabilities = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <h2 className="text-[#272422] text-[28px] lg:text-[40px] font-medium tracking-tighter mb-2">Agent capabilities</h2>
        <p className="text-[#888] text-[20px] lg:text-[24px] tracking-tight mb-12">Translate user intents directly into actions</p>
        <div className="flex flex-col gap-16">
          {capabilities.map((c, i) => (
            <div key={i} className={`flex flex-col lg:flex-row-reverse items-center gap-8 lg:gap-12 bg-[#F5F3F0] rounded-xl p-6 lg:p-10 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 150}ms` }}>
              {/* Right: Image + Eval */}
              <div className="w-full lg:w-1/2 flex flex-col gap-4">
                <div className="aspect-[3/2] rounded-lg overflow-hidden border border-[#272422]/20">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="bg-[#F9F7F4] p-4 rounded-lg border border-[#272422]/20">
                  <p className="uppercase font-mono font-bold text-[#666] text-[12px] tracking-wider mb-2">Eval</p>
                  <div className="flex items-center justify-between py-1 font-mono font-bold text-[#0D7149] text-[14px]">
                    <span>{c.label}</span><span>{c.score}</span>
                  </div>
                  {c.comparison && (
                    <div className="flex items-center justify-between py-1 font-mono text-[14px] text-[#272422]/60 border-t border-dashed border-[#272422]/20">
                      <span>{c.comparison.label}</span><span>{c.comparison.score}</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Left: Text */}
              <div className="w-full lg:w-1/2">
                <h2 className="text-[#272422] text-[28px] lg:text-[40px] font-medium tracking-tighter leading-tight mb-3">{c.title}</h2>
                <p className="text-[#272422] text-[16px] lg:text-[20px] tracking-tight">{c.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════ DECCAN CUSTOM SOLUTIONS (dark, stacked cards) ════════════ */
const solutions = [
  { title: 'Social Media Agent', img: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67e14bba7155a978011fadff_Frame%201597882215%20(2).avif' },
  { title: 'Conversational Voice Agent', img: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67e155d8742fce93f4f4b796_Group%201000009149.avif' },
  { title: 'Conversational Chat Agent', img: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67e156202871ae7be666bc2f_Group%201000009283.avif' },
];

const DeccanSolutions = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#0a0a0a] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <h2 className="text-white text-[36px] lg:text-[48px] font-light mb-2">
          Custom <span className="bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent">Solutions</span>
        </h2>
        <p className="text-white/50 text-[18px] lg:text-[20px] mb-12">Every business is unique—your AI agents should be too.</p>
        <div className="grid md:grid-cols-3 gap-6">
          {solutions.map((s, i) => (
            <div key={i} className={`bg-[#1a1a1a] border border-white/10 rounded-2xl overflow-hidden transition-all duration-700 hover:border-white/30 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 150}ms` }}>
              <div className="p-6 pb-4">
                <h3 className="text-white text-[24px] lg:text-[32px] font-light">{s.title}</h3>
              </div>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/get-in-touch" className="inline-block px-8 py-3 border border-white/30 text-white rounded-full text-[16px] hover:bg-white/10 transition-colors">
            Build Your AI Agent
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ════════════ ADEPT USE CASES (light, accordion + images) ════════════ */
const useCases = [
  { title: 'Supply-chain', desc: 'Check shipping availability across hundreds of sites at once to have accurate delivery plans put together instantly.', imgs: ['https://adept.ai/_astro/1.D3EATS2k_ByLGv.png', 'https://adept.ai/_astro/2.D5UtrWKc_Z1qdbPM.png'] },
  { title: 'Financial Services', desc: 'Extract key info from PDFs/contracts, accurately update internal systems, and send an email when the work is done.', imgs: ['https://adept.ai/_astro/1.CA4u3xty_9hUYf.png', 'https://adept.ai/_astro/2.lRjYUsRZ_ZEV3EE.png'] },
  { title: 'Healthcare', desc: 'Process license applications that adhere to business logic, keeping a human in the loop to submit the final application.', imgs: ['https://adept.ai/_astro/1.BikHOBKe_Bea0.png', 'https://adept.ai/_astro/2.CUJG4de5_QGqRb.png'] },
];

const AdeptUseCases = () => {
  const [active, setActive] = useState(0);
  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <p className="text-[#888] text-[20px] lg:text-[24px] tracking-tight mb-1">Examples of what we can do</p>
        <h2 className="text-[#272422] text-[28px] lg:text-[40px] font-medium tracking-tighter mb-12">In action</h2>
        <div className="flex flex-col md:flex-row gap-8 lg:gap-16">
          {/* Left: Accordion */}
          <div className="md:w-1/2 flex flex-col gap-2">
            {useCases.map((uc, i) => (
              <div key={i} onClick={() => setActive(i)}
                className={`rounded-lg p-5 cursor-pointer transition-colors duration-300 ${active === i ? 'bg-[#F5F3F0]' : 'bg-white hover:bg-[#F5F3F0]/50'}`}>
                <h3 className="text-[#272422] text-[20px] lg:text-[24px] tracking-tight">{uc.title}</h3>
                {active === i && <p className="text-[#272422] text-[15px] lg:text-[16px] tracking-tight mt-3 leading-snug">{uc.desc}</p>}
              </div>
            ))}
            <p className="text-[15px] text-[#272422] mt-6">
              <Link to="/get-in-touch" className="underline underline-offset-4 hover:opacity-70">Contact us</Link> to share your use case with us.
            </p>
          </div>
          {/* Right: Image */}
          <div className="md:w-1/2">
            <div className="aspect-square rounded-lg overflow-hidden bg-[#FBF9F6] border border-[#F5F3F0]">
              <img src={useCases[active].imgs[0]} alt={useCases[active].title} className="w-full h-full object-cover transition-opacity duration-500" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ════════════ ADEPT STRENGTHS (off-white bg, colored dots) ════════════ */
const strengths = [
  { dot: '#FFB345', title: 'Accurate & reliable', desc: 'Using our proprietary model, we provide more accurate and reliable workflows than competitors.' },
  { dot: '#FFB664', title: 'Future-proof', desc: 'Our workflows are resilient to changes in your environment, and require no maintenance or upkeep.' },
  { dot: '#FEBBA0', title: 'Get up and running quickly', desc: 'We use natural language instructions to set up new workflows in minutes, not months.' },
  { dot: '#F5AAB5', title: 'Enterprise wide', desc: 'Unlock value across your organization—empowering every department, from HR to Operations.' },
];

const AdeptStrengths = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#F5F3F0] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <div className="flex flex-col md:flex-row gap-10">
          <div className="md:w-1/2">
            <p className="text-[#888] text-[20px] lg:text-[24px] tracking-tight mb-1">What makes us different</p>
            <h2 className="text-[#272422] text-[28px] lg:text-[40px] font-medium tracking-tighter mb-8">Our strengths</h2>
            <div className="flex flex-col gap-8 pl-6">
              {strengths.map((s, i) => (
                <div key={i} className={`relative transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: `${i * 200}ms` }}>
                  <div className="absolute -left-6 top-[8px] w-[11px] h-[11px] rounded-full" style={{ backgroundColor: s.dot }} />
                  <h3 className="text-[#272422] text-[20px] lg:text-[24px] tracking-tight mb-2">{s.title}</h3>
                  <p className="text-[#272422] text-[16px] lg:text-[18px] tracking-tight leading-snug max-w-lg">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="md:w-1/2">
            <div className="aspect-square overflow-hidden rounded-lg md:mt-16">
              <img src="https://adept.ai/_astro/were-different.DITqWfaU_Zu6BRn.png" alt="Abstract machine" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ════════════ DECCAN STUDIO (dark, 4 cards grid) ════════════ */
const studioCards = [
  { title: 'Build', desc: "Create customized AI agents using predefined templates and your organization's data" },
  { title: 'Design', desc: 'Build customer friendly UI for AI agents using our in-house UI Builder' },
  { title: 'Test', desc: 'Orchestrate and Monitor AI agents in real-time to fine tune them according to your needs' },
  { title: 'Observe', desc: 'Evaluate AI agents in our Sandbox environment to simulate real-world scenarios' },
];

const DeccanStudio = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#0a0a0a] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <h2 className="text-white text-[36px] lg:text-[48px] font-light mb-2">
          Duty AI <span className="bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent">Studio</span>
        </h2>
        <p className="text-white/50 text-[18px] lg:text-[20px] mb-12">Our in-house platform enables you to build AI agents to enhance your workflow</p>
        <div className="grid md:grid-cols-2 gap-6">
          {studioCards.map((c, i) => (
            <div key={i} className={`bg-[#1a1a1a] border border-white/10 rounded-xl p-8 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 120}ms` }}>
              <h3 className="text-white text-[28px] lg:text-[32px] font-light mb-3">{c.title}</h3>
              <p className="text-white/50 text-[15px] leading-[1.6]">{c.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════ DECCAN DEVELOPMENT PROCESS (dark, numbered) ════════════ */
const devSteps = [
  { num: '1', title: 'Understand', desc: 'Deep-dive session to analyze your business needs, data sources, and AI goals to define a clear roadmap.' },
  { num: '2', title: 'Customize & Train', desc: "We fine-tune AI models with your organization's data, ensuring personalized responses and seamless system integration." },
  { num: '3', title: 'Deploy & Optimize', desc: 'Seamless AI deployment with real-time monitoring and continuous improvements based on actionable insights.' },
];

const DeccanProcess = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#0a0a0a] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <h2 className="text-white text-[36px] lg:text-[48px] font-light mb-2">
          Custom <span className="bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent">Multi-Agent</span> Workflows
        </h2>
        <p className="text-white/50 text-[18px] lg:text-[20px] mb-6">Custom-built AI agents adapt to your business needs</p>
        <Link to="/get-in-touch" className="inline-block px-8 py-3 border border-white/30 text-white rounded-full text-[16px] hover:bg-white/10 transition-colors mb-14">
          Talk to Us
        </Link>
        <div className="grid md:grid-cols-3 gap-8">
          {devSteps.map((s, i) => (
            <div key={i} className={`transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 200}ms` }}>
              <div className="text-[80px] lg:text-[100px] font-light leading-none bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent mb-[-10px]">
                {s.num}.
              </div>
              <h3 className="text-white text-[22px] lg:text-[24px] font-normal mb-3">{s.title}</h3>
              <p className="text-white/50 text-[15px] leading-[1.6]">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════ DECCAN WHY (dark, icon cards) ════════════ */
const whyItems = [
  { icon: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67dea1ee9813cb129ffad950_Percent.svg', title: 'Proven Impact', desc: "We've trained top-tier LLMs, bringing world-class AI expertise to your business" },
  { icon: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67dea1fcc8f20e3b83794657_Brain.svg', title: 'Smarter and Efficient', desc: 'Our AI streamlines workflows and boosts efficiency with human-like engagement' },
  { icon: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67dea20e050dd1d0884f6671_Sliders%20(1).svg', title: 'Tailored to Your Business', desc: "Trained on your organization's data, our agents align with your brand identity for personalized responses" },
  { icon: 'https://cdn.prod.website-files.com/66d017bee914d25aa0a45529/67dea22722d52b417b5fcbde_Robot.svg', title: 'Custom-Built AI Agents', desc: 'Customized with the features and capabilities that fit your business perfectly' },
];

const DeccanWhy = () => {
  const [ref, vis] = useAnimateIn();
  return (
    <section ref={ref} className="bg-[#0a0a0a] py-16 lg:py-24">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
        <h2 className="text-white text-[36px] lg:text-[48px] font-light mb-3">Why Duty AI</h2>
        <p className="text-white/50 text-[18px] lg:text-[20px] mb-14">Every business is unique—your AI agents should be too</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {whyItems.map((w, i) => (
            <div key={i} className={`bg-[#1a1a1a] border border-white/10 rounded-xl p-6 transition-all duration-700 ${vis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: `${i * 120}ms` }}>
              <img src={w.icon} alt={w.title} className="w-10 h-10 mb-5 invert" loading="lazy" />
              <h3 className="text-white text-[22px] lg:text-[24px] font-light mb-3">{w.title}</h3>
              <p className="text-white/50 text-[14px] leading-[1.6]">{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ════════════ ADEPT TRUST (off-white) ════════════ */
const AdeptTrust = () => (
  <section className="bg-[#F5F3F0] py-12 lg:py-20">
    <div className="max-w-[1200px] mx-auto px-6 lg:px-16">
      <p className="text-[#888] text-[20px] tracking-tight mb-1">An organization built on trust</p>
      <h2 className="text-[#272422] text-[28px] lg:text-[48px] font-medium tracking-tighter mb-6">Trust and security</h2>
      <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6">
        <p className="text-[#272422] text-[16px] lg:text-[20px] tracking-tight max-w-xl">
          Committed to building trust by protecting customer data, models, and products with enterprise-grade security.
        </p>
        <Link to="/get-in-touch" className="inline-block px-8 py-3 border border-[#272422] text-[#272422] rounded-full text-[16px] hover:bg-[#272422]/5 transition-colors whitespace-nowrap">
          Visit our Trust Center →
        </Link>
      </div>
    </div>
  </section>
);

/* ════════════ DECCAN CTA (dark, video bg) ════════════ */
const DeccanCTA = () => (
  <section className="relative bg-[#0a0a0a] py-20 lg:py-32 overflow-hidden">
    <video className="absolute inset-0 w-full h-full object-cover opacity-20" autoPlay muted loop playsInline
      poster="https://cdn.prod.website-files.com/66d017bee914d25aa0a45529%2F66d7f4d5c5a797b9127e90bc_footer-vide-poster-00001.jpg">
      <source src="https://cdn.prod.website-files.com/66d017bee914d25aa0a45529%2F66d7f4d5c5a797b9127e90bc_footer-vide-transcode.mp4" type="video/mp4" />
    </video>
    <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-16 text-center">
      <h2 className="text-[36px] lg:text-[56px] font-light mb-2">
        <span className="bg-gradient-to-r from-[#175fff] to-[#8B5CF6] bg-clip-text text-transparent">Build Your AI, Your Way</span>
      </h2>
      <p className="text-white text-[28px] lg:text-[40px] font-light mb-10">No More One-Size-Fits-All</p>
      <Link to="/get-in-touch" className="inline-block px-10 py-4 border border-white/30 text-white rounded-full text-[18px] hover:bg-white/10 transition-colors">
        Talk to Us
      </Link>
    </div>
  </section>
);

/* ════════════ PAGE ════════════ */
const AIAgentsPage = () => (
  <>
    <Navbar />
    <DeccanHero />
    <DeccanPillars />
    <AdeptStack />
    <AdeptCapabilities />
    <DeccanSolutions />
    <AdeptUseCases />
    <DeccanStudio />
    <DeccanProcess />
    <AdeptStrengths />
    <DeccanWhy />
    <AdeptTrust />
    <DeccanCTA />
    <Footer />
  </>
);

export default AIAgentsPage;
